/**
 * Pulsante della barra di pre-elaborazione (volantino e variante) che apre la mini legenda
 * delle scorciatoie da tastiera e dei gesti del mouse sulle gabbie. Una riga per azione:
 * cosa fare a sinistra, come farlo a destra. I tasti seguono il sistema in uso (⌘ Cmd, ⌥ Option,
 * ⇧ Shift su Mac; Ctrl, Alt, Shift altrove: le scorciatoie accettano entrambi).
 * Tenere allineato con i gestori tastiera delle due pagine quando se ne aggiunge una.
 * @module flyers/components/ShortcutsLegend
 */

import React from "react";
import {Button, Popover, Tooltip, Typography} from "antd";
import {MacCommandOutlined} from "@ant-design/icons";
import "./ShortcutsLegend.css";

const {Text} = Typography;

const IS_MAC = typeof navigator !== "undefined"
	&& /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || "");

// Elementi di una combinazione: tasto ({key, name?}) oppure gesto del mouse ({mouse}).
const CMD = IS_MAC ? {key: "⌘", name: "Cmd"} : {key: "Ctrl"};
const OPT = IS_MAC ? {key: "⌥", name: "Option"} : {key: "Alt"};
const SHIFT = IS_MAC ? {key: "⇧", name: "Shift"} : {key: "Shift"};
const DEL = IS_MAC ? {key: "⌫", name: "delete"} : {key: "Canc"};
const PERIOD = {key: ".", name: "punto"};
const COMMA = {key: ",", name: "virgola"};
const ARROWS = {key: "← ↑ → ↓"};
const key = (k) => ({key: k});
const mouse = (text) => ({mouse: text});

// Ogni riga: azione + combinazioni alternative (ognuna è un elenco di elementi).
const BASE_SECTIONS = [
	{
		title: "Selezione",
		rows: [
			{label: "Selezionare una gabbia", combos: [[mouse("clic")]]},
			{label: "Aggiungere o togliere dalla selezione", combos: [[CMD, mouse("clic")], [SHIFT, mouse("clic")]]},
			{label: "Selezionare con un rettangolo, partendo da un'area vuota", combos: [[CMD, mouse("trascina")], [SHIFT, mouse("trascina")]]}
		]
	},
	{
		title: "Spostare",
		rows: [
			{label: "Spostare tutta la selezione", combos: [[mouse("trascina una gabbia selezionata")]]},
			{label: "Spostare di 1 px", combos: [[ARROWS]]},
			{label: "Spostare di 10 px", combos: [[SHIFT, ARROWS]]}
		]
	},
	{
		title: "Ridimensionare",
		rows: [
			{label: "Ridimensionare tutta la selezione", combos: [[mouse("trascina una maniglia")]]},
			{label: "Ridimensionare mantenendo le proporzioni", combos: [[SHIFT, mouse("trascina una maniglia")]]},
			{label: "Ingrandire dell'1%", combos: [[CMD, PERIOD]]},
			{label: "Rimpicciolire dell'1%", combos: [[CMD, COMMA]]},
			{label: "Ingrandire del 5%", combos: [[OPT, CMD, PERIOD]]},
			{label: "Rimpicciolire del 5%", combos: [[OPT, CMD, COMMA]]}
		]
	},
	{
		title: "Modificare",
		rows: [
			{label: "Copiare", combos: [[CMD, key("C")]]},
			{label: "Incollare vicino al puntatore", combos: [[CMD, key("V")]]},
			{label: "Incollare nella stessa posizione", combos: [[OPT, SHIFT, CMD, key("V")]]},
			{label: "Eliminare", combos: [[DEL]]},
			{label: "Annullare", combos: [[CMD, key("Z")]]},
			{label: "Ripristinare", combos: [[SHIFT, CMD, key("Z")]]}
		]
	}
];

// Gesti presenti solo nella pre-elaborazione della variante.
const VARIANT_SECTION = {
	title: "Variante",
	rows: [
		{label: "Copiare nella variante una gabbia del riferimento", combos: [[mouse("doppio clic sul riferimento")]]},
		{label: "Segnare un'ereditata da rielaborare, o tornare indietro", combos: [[mouse("doppio clic sulla variante")]]}
	]
};

function ComboPart({part})
{
	if (part.mouse)
		return <span className="shortcuts-legend-mouse">{part.mouse}</span>;
	return (
		<span className="shortcuts-legend-key">
			<span className="shortcuts-legend-key-symbol">{part.key}</span>
			{part.name && <span className="shortcuts-legend-key-name">{part.name}</span>}
		</span>
	);
}

// Elementi di una combinazione uniti da "+"; alternative separate da "oppure".
function Combos({combos})
{
	return (
		<span className="shortcuts-legend-keys">
			{combos.map((combo, i) => (
				<React.Fragment key={i}>
					{i > 0 && <span className="shortcuts-legend-or">oppure</span>}
					<span className="shortcuts-legend-combo">
						{combo.map((part, j) => (
							<React.Fragment key={j}>
								{j > 0 && <span className="shortcuts-legend-plus">+</span>}
								<ComboPart part={part}/>
							</React.Fragment>
						))}
					</span>
				</React.Fragment>
			))}
		</span>
	);
}

function LegendContent({sections})
{
	return (
		<div className="shortcuts-legend">
			<div className="shortcuts-legend-columns">
				{sections.map((section) => (
					<div key={section.title} className="shortcuts-legend-section">
						<Text strong className="shortcuts-legend-title">{section.title}</Text>
						{section.rows.map((row) => (
							<div key={row.label} className="shortcuts-legend-row">
								<span className="shortcuts-legend-label">{row.label}</span>
								<Combos combos={row.combos}/>
							</div>
						))}
					</div>
				))}
			</div>
			<Text type="secondary" className="shortcuts-legend-note">
				Le scorciatoie non agiscono mentre si scrive in un campo di testo.
			</Text>
		</div>
	);
}

export default function ShortcutsLegend({variant = false, size})
{
	const sections = variant ? [...BASE_SECTIONS, VARIANT_SECTION] : BASE_SECTIONS;
	return (
		<Popover trigger="click" placement="bottom" content={<LegendContent sections={sections}/>}>
			<Tooltip title="Scorciatoie da tastiera">
				<Button size={size} icon={<MacCommandOutlined/>} aria-label="Scorciatoie da tastiera"/>
			</Tooltip>
		</Popover>
	);
}
